/** F3 · Per-device Web Push opt-in card (used by student & coach settings). */

import { useEffect, useState } from 'react';
import { t } from '../lib/i18n.ts';
import { disablePush, enablePush, getPushSubscription, pushSupported } from '../lib/push.ts';
import { Button, Card, ErrorText } from '../lib/ui.tsx';

export default function PushCard() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    void getPushSubscription().then((sub) => setEnabled(!!sub));
  }, []);

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
      } else {
        await enablePush();
        setEnabled(true);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('settings.push', 'Push notifications')}>
      {!pushSupported() ? (
        <p className="text-xs text-slate-500">
          {t('settings.pushUnsupported', 'This browser does not support push notifications.')}
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            {t(
              'settings.pushHint',
              'Get reminders, review results and messages as real notifications on this device — even when Solfo is closed. Which types arrive is controlled per type below.',
            )}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Button variant={enabled ? 'secondary' : 'primary'} onClick={() => void toggle()} disabled={busy || enabled === null}>
              {busy
                ? '…'
                : enabled
                  ? t('settings.pushDisable', 'Disable on this device')
                  : t('settings.pushEnable', 'Enable on this device')}
            </Button>
            {enabled && <span className="text-xs text-emerald-700">{t('settings.pushOn', 'Active on this device ✓')}</span>}
          </div>
        </>
      )}
      <ErrorText error={error} />
    </Card>
  );
}
